import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { FiArrowLeft, FiMail, FiLock, FiShield, FiLoader, FiCheckCircle, FiCreditCard, FiExternalLink, FiAlertTriangle } = FiIcons;
import supabase from '../supabase/supabase';

const Account = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      setLoading(false);
    };
    fetchUser();
  }, []);

  const handlePasswordReset = async () => {
    if (!user?.email) return;
    setResetLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: `${window.location.origin}/dashboard`,
      });
      if (error) throw error;
      setResetSent(true);
      setTimeout(() => setResetSent(false), 5000);
    } catch (err) {
      setError(err.message || "Failed to send reset link");
    } finally {
      setResetLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    setError(null);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('create-portal-session');
      
      if (invokeError) throw invokeError;

      if (data && data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No redirection URL returned from the server.");
      }
    } catch (err) {
      console.error("Billing Portal Error:", err);
      setError("Could not open billing portal. Please contact support at support@etera.store.");
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f4f4f4]">
        <SafeIcon icon={FiLoader} className="w-12 h-12 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f4f4] py-12 px-6 font-sans">
      <div className="max-w-4xl mx-auto">
        <div className="mb-12">
          <button 
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 font-black uppercase text-sm hover:underline decoration-2 underline-offset-4 mb-8"
          >
            <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
            Back to Dashboard
          </button>
          <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tighter leading-none mb-2 text-left text-black">
            Account Settings
          </h1>
          <p className="font-bold text-gray-500 uppercase text-xs tracking-[0.2em] text-left">Manage your profile and security</p>
        </div>

        {error && (
          <div className="bg-red-100 border-4 border-black p-4 mb-8 flex items-center gap-3 brutal-shadow-sm text-left">
            <SafeIcon icon={FiAlertTriangle} className="w-6 h-6 text-red-600 shrink-0" />
            <p className="font-black uppercase text-sm">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 gap-8">
          <div className="bg-white border-4 border-black p-8 brutal-shadow">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-black text-white">
                <SafeIcon icon={FiMail} className="w-6 h-6" />
              </div>
              <h2 className="text-xl font-black uppercase">Email Address</h2>
            </div>
            <div className="space-y-1 text-left">
              <p className="text-xs font-black uppercase text-gray-400 tracking-widest">Current Email</p>
              <p className="text-2xl font-black break-all">{user?.email}</p>
            </div>
          </div>

          <div className="bg-white border-4 border-black p-8 brutal-shadow text-left">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-black text-white">
                <SafeIcon icon={FiShield} className="w-6 h-6" />
              </div>
              <h2 className="text-xl font-black uppercase">Security</h2>
            </div>
            <div>
              <h3 className="font-black uppercase text-sm mb-2">Password</h3>
              <p className="font-bold text-gray-600 mb-6">
                Need a new password? We'll send a secure reset link to your email.
              </p>
              <button 
                onClick={handlePasswordReset}
                disabled={resetLoading || resetSent}
                className={`flex items-center justify-center gap-2 px-8 py-4 font-black uppercase border-2 border-black brutal-shadow-sm brutal-hover transition-all w-full sm:w-auto ${
                  resetSent ? 'bg-green-400 text-black' : 'bg-black text-white'
                }`}
              >
                {resetLoading ? (
                  <SafeIcon icon={FiLoader} className="animate-spin" />
                ) : resetSent ? (
                  <><SafeIcon icon={FiCheckCircle} /> Link Sent!</>
                ) : (
                  <><SafeIcon icon={FiLock} /> Send Reset Link</>
                )}
              </button>
            </div>
          </div>

          <div className="bg-white border-4 border-black p-8 brutal-shadow text-left">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-black text-white">
                <SafeIcon icon={FiCreditCard} className="w-6 h-6" />
              </div>
              <h2 className="text-xl font-black uppercase">Billing & Invoices</h2>
            </div>
            <div className="space-y-6">
              <p className="font-bold text-gray-600">
                View your purchase history, download invoices, and manage your payment methods through our secure Stripe portal.
              </p>
              <button 
                onClick={handleManageBilling}
                disabled={portalLoading}
                className="flex items-center justify-center gap-3 px-8 py-4 font-black uppercase border-2 border-black bg-white text-black brutal-shadow-sm brutal-hover transition-all w-full sm:w-auto"
              >
                {portalLoading ? (
                  <><SafeIcon icon={FiLoader} className="animate-spin" /> Redirecting...</>
                ) : (
                  <><SafeIcon icon={FiExternalLink} /> Manage Billing & Invoices</>
                )}
              </button>
              <p className="text-xs font-bold text-gray-400 uppercase">
                Issues? Contact <a href="mailto:support@etera.store" className="underline decoration-1">support@etera.store</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Account;